/**
 * Forgotten passwords.
 *
 *   member types a mobile number OR an email
 *     → we find the member, and therefore the EMAIL on their record
 *     → a one-time reset link is emailed to that address (SendGrid, from the API)
 *     → the screen shows the MASKED address so they know which inbox to open
 *     → the link opens /reset/:token, they set a new password, link dies
 *
 * The rules, enforced here and tested in reset.test.js:
 *   - A reset link is ONE-TIME. Setting a password spends it.
 *   - It expires after RESET_TTL_MIN minutes.
 *   - Asking again voids every earlier link for that member, so a forwarded
 *     or shoulder-surfed older email is already dead.
 *   - A member with no email on file cannot reset — that is why email is
 *     mandatory on every form that creates a member.
 *   - A member who has left the club cannot reset their way back in.
 *
 * These are pure draft-mutators, like join.js: they take the cloned db that
 * ClubContext's `commit` hands out plus any ids the caller allocated. Against
 * the real backend the same logic runs inside API transactions and the
 * password lands in Firebase Auth, not in a document.
 */

import { normalizeEmail } from './email';
import { passwordProblem } from './join';

/** How long a reset link stays usable. Long enough to find the email. */
export const RESET_TTL_MIN = 60;
const RESET_TTL_MS = RESET_TTL_MIN * 60 * 1000;

/*
 * A reset entry lives in `db.resets`:
 *   { id, token, memberId, email, createdAt, expiresAt, status }
 * where status is 'active' | 'used' | 'void'.
 */

/** Why a member cannot be sent a reset link, or null if they can. */
export function resetBlocker(member) {
  if (!member) return 'NO_ACCOUNT';
  if (member.status === 'left') return 'LEFT_CLUB';
  if (!normalizeEmail(member.email)) return 'NO_EMAIL';
  return null;
}

export const RESET_BLOCKER_MESSAGE = {
  NO_ACCOUNT:
    'We could not find a membership with that mobile number or email. Check it, or request membership.',
  LEFT_CLUB: 'That membership is no longer active. Please contact the committee.',
  NO_EMAIL:
    'There is no email address on that membership, so we cannot send a reset link. Please contact the committee.',
};

/**
 * Issues a reset link for a member. Any earlier active link is voided — the
 * older email stops working the moment a newer one exists.
 *
 * Throws rather than returning a code: the caller has already checked
 * `resetBlocker`, so reaching here with a bad member is a bug, not a typo.
 */
export function createResetToken(draft, memberId, token, now = Date.now()) {
  const member = draft.members.find((m) => m.id === memberId);
  const blocker = resetBlocker(member);
  if (blocker) throw new Error(RESET_BLOCKER_MESSAGE[blocker]);

  draft.resets ??= [];
  for (const entry of draft.resets) {
    if (entry.memberId === memberId && entry.status === 'active') entry.status = 'void';
  }

  const entry = {
    id: `rst_${token}`,
    token,
    memberId,
    email: normalizeEmail(member.email),
    createdAt: now,
    expiresAt: now + RESET_TTL_MS,
    status: 'active',
  };
  draft.resets.push(entry);
  return entry;
}

/** The reset entry behind a token, or null. */
export function resetByToken(db, token) {
  return (db.resets ?? []).find((r) => r.token === token) ?? null;
}

/**
 * What a link is worth right now: 'ok' | 'unknown' | 'used' | 'void' |
 * 'expired'. The /reset screen renders a different page for each, because
 * "this link was already used" and "this link expired" send the member to
 * different next steps.
 */
export function resetTokenState(entry, now = Date.now()) {
  if (!entry) return 'unknown';
  if (entry.status === 'used') return 'used';
  if (entry.status === 'void') return 'void';
  if (now > entry.expiresAt) return 'expired';
  return 'ok';
}

export const RESET_STATE_MESSAGE = {
  unknown: 'This reset link is not valid. Ask for a new one below.',
  used: 'This link has already been used to set a password. Ask for a new one if you need it.',
  void: 'A newer reset link was sent, so this one no longer works. Open the most recent email.',
  expired: `This link expired — reset links last ${RESET_TTL_MIN} minutes. Ask for a new one below.`,
};

/**
 * Spends the link and sets the new password. Returns the member.
 *
 * The password is validated BEFORE anything is written, so a member who
 * fumbles the confirmation still has a live link to try again with.
 */
export function consumeResetToken(draft, token, password, now = Date.now()) {
  const entry = resetByToken(draft, token);
  const state = resetTokenState(entry, now);
  if (state !== 'ok') throw new Error(RESET_STATE_MESSAGE[state]);

  const problem = passwordProblem(password);
  if (problem) throw new Error(problem);

  const member = draft.members.find((m) => m.id === entry.memberId);
  if (!member) throw new Error('That membership is no longer on the roster.');
  if (member.status === 'left') {
    throw new Error('That membership is no longer active. Please contact the committee.');
  }

  member.password = password;
  entry.status = 'used';
  entry.usedAt = now;
  return member;
}
